import { Link } from "@tanstack/react-router";
import { BombIcon } from "@signet/ui";
import { defineWebModule, useMe, type NowCardData } from "@signet/web-kit";
import { CreatorChip, useIgsCreatorPhotos } from "./shared.tsx";
import { str } from "./api.ts";
import { AccountsPage } from "./accounts/page.tsx";
import { OverviewPage } from "./overview.tsx";
import { ReviewPage } from "./review.tsx";
import { SetupPage } from "./creator-setup.tsx";
import "./igsuicide.css";

/* ──────────────────────────────────────────────────────────────────────────
   The web half of IG Suicide: Growth, Ban check and Accounts. It is on the bar
   (Jay, 2026-10-12; under More before), with a bomb for its icon,
   as Jay asked (2026-09-30).
   ────────────────────────────────────────────────────────────────────────── */

type ToCheck = { id: string; handle: string; creator: string | null; source: "sheet" | "phone"; phone: string | null };

/* Only the viewer's own accounts for a poster, everyone's for an admin (server/to-check.ts, Jay 2026-10-12). */
function ReviewNowCard({ card }: { card: NowCardData }) {
  const data = card.data as { missing: number; accounts: ToCheck[]; mine?: boolean };
  const photos = useIgsCreatorPhotos();
  const { can } = useMe();
  const onPhones = data.accounts.some((a) => a.source === "phone");
  return (
    <section className="panel now-card">
      <div className="panel-head">
        <h2>{card.title}</h2>
      </div>
      <div className="panel-body">
        <p className="now-figure tabular">{data.missing}</p>
        <p className="muted" style={{ fontSize: 10 }}>
          {data.mine ? "Your accounts" : "Accounts"} Instagram can't find on their last read. Check each on its phone, then mark it banned or fine.
        </p>
        <ul className="now-list">
          {data.accounts.map((a) => (
            <li key={`${a.source}-${a.id}`}>
              {a.source === "phone" ? (
                <Link to={"/ig-suicide/accounts" as never} search={{ open: a.id } as never}>@{a.handle}</Link>
              ) : (
                // Opens the account on Instagram so it can be checked from the card (Ben, 2026-10-29).
                <a href={`https://www.instagram.com/${encodeURIComponent(a.handle)}/`} target="_blank" rel="noreferrer">@{a.handle}</a>
              )}
              {a.creator ? <>, <CreatorChip name={a.creator} photos={photos} size={14} /></> : null}
              {a.phone ? <span className="muted">, phone {a.phone}</span> : null}
            </li>
          ))}
        </ul>
        {data.accounts.length < data.missing ? <p className="muted" style={{ fontSize: 10 }}>and {data.missing - data.accounts.length} more</p> : null}
        <Link to={(can("igsuicide.account:view") && !onPhones ? "/ig-suicide/review" : "/ig-suicide/accounts") as never} className="text-button tiny accent">Check them</Link>
      </div>
    </section>
  );
}

export default defineWebModule({
  id: "igsuicide",
  // Posters may hold only the Accounts tab's permission; the Growth page sends them on to it.
  // Opens on Accounts (Jay, 2026-10-08); anyone without Accounts is sent on to Growth from there.
  nav: { label: "IG Suicide", icon: BombIcon, to: "/ig-suicide/accounts", needs: ["igsuicide.account:view", "igsuicide.stock:view"] },
  routes: [
    {
      path: "/ig-suicide",
      component: OverviewPage,
      needs: ["igsuicide.account:view", "igsuicide.stock:view"],
      // The router reads ?span=7 back as a number, so it is turned into text here.
      validateSearch: (raw) => ({ span: raw.span === undefined || raw.span === null ? undefined : String(raw.span), from: str(raw.from), to: str(raw.to), creator: str(raw.creator) }),
    },
    { path: "/ig-suicide/review", component: ReviewPage, needs: "igsuicide.account:view" },
    { path: "/ig-suicide/setup", component: SetupPage, needs: "igsuicide.account:view" },
    {
      path: "/ig-suicide/accounts",
      component: AccountsPage,
      needs: ["igsuicide.stock:view", "igsuicide.account:view"],
      validateSearch: (raw) => ({ view: str(raw.view), who: str(raw.who), phone: str(raw.phone), open: str(raw.open) }),
    },
  ],
  commands: [
    { id: "igsuicide.growth", label: "IG Suicide growth", to: "/ig-suicide", needs: "igsuicide.account:view", hint: "IG Suicide", keywords: "instagram throwaway accounts followers views growth banned" },
    { id: "igsuicide.accounts", label: "IG Suicide accounts", to: "/ig-suicide/accounts", needs: "igsuicide.stock:view", hint: "IG Suicide", keywords: "bought accounts phones warm-up aged soft reg upload setup test sop" },
    { id: "igsuicide.setup", label: "IG Suicide setup", to: "/ig-suicide/setup", needs: "igsuicide.account:view", hint: "IG Suicide", keywords: "duplication link unique link required accounts creators" },
    { id: "igsuicide.review", label: "IG Suicide ban check", to: "/ig-suicide/review", needs: "igsuicide.account:view", hint: "IG Suicide", keywords: "banned not found instagram accounts" },
  ],
  nowCards: { "igsuicide.review": ReviewNowCard },
});
